import { useApiAuthActions } from '@cretesuite/api-client-auth'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useGraphQLCache } from '../context/GraphQLProvider/Cache'
import { reloadTo } from '../helpers/appReload'
import { useSyncStatus } from '../context/SyncStatusProvider'
import { getTokenClaims } from '../helpers/authToken'
import { useNetwork } from './useNetwork'

export type OrgSwitchBlockedReason = 'offline' | 'pending-sync' | 'syncing'

export type OrgSwitch = {
  /** Organization id the current access token is scoped to. */
  activeTid: number | null
  /** Why switching is currently blocked, or null when allowed. */
  blockedReason: OrgSwitchBlockedReason | null
  /** True from the first tap until the switch fails or the app reloads. */
  switching: boolean
  /** Switch to another organization. Resolves false when blocked or rejected. */
  switchTo: (tid: number) => Promise<boolean>
}

/**
 * Organization switching. A switch exchanges the auth tokens server-side, so it
 * can never be queued offline — it is fully blocked while offline and while
 * queued offline mutations are still pending (they belong to the current org
 * and would be lost by the cache wipe).
 *
 * On success the persisted graphcache is wiped and the app fully reloads to
 * /welcome, mirroring the login flow — the reboot re-runs `me` under the new
 * token, which re-seeds organization branding.
 */
export const useOrgSwitch = (): OrgSwitch => {
  const { isOnline } = useNetwork()
  const { pendingCount, isSyncing } = useSyncStatus()
  const { switchOrganization, getAccessToken } = useApiAuthActions()
  const { clear } = useGraphQLCache()
  const [switching, setSwitching] = useState(false)
  const switchInFlight = useRef(false)

  const activeTid = getTokenClaims(getAccessToken()).tid

  const blockedReason = useMemo<OrgSwitchBlockedReason | null>(() => {
    if (!isOnline) return 'offline'
    if (pendingCount > 0) return 'pending-sync'
    if (isSyncing) return 'syncing'
    return null
  }, [isOnline, pendingCount, isSyncing])

  const runSwitch = useCallback(
    async (tid: number): Promise<boolean> => {
      // Re-selecting the active org is pure navigation — allowed even blocked
      if (tid === activeTid) {
        reloadTo('/welcome')
        return true
      }

      if (blockedReason !== null) return false

      let token: string | null
      try {
        token = await switchOrganization(tid)
      } catch (error) {
        console.error('Organization switch failed:', error)
        return false
      }
      // Rejected switches surface through the ApiAuth onError toast — keep the
      // current org and its cache untouched.
      if (!token) return false

      await clear()
      reloadTo('/welcome')
      return true
    },
    [blockedReason, activeTid, switchOrganization, clear],
  )

  const switchTo = useCallback(
    async (tid: number): Promise<boolean> => {
      // A ref, not the state below: two taps in the same tick would both read
      // `switching` as false and start concurrent token swaps.
      if (switchInFlight.current) return false
      switchInFlight.current = true
      setSwitching(true)

      const switched = await runSwitch(tid)
      // Stay locked on success — the app is reloading, nothing may re-enter.
      if (!switched) {
        switchInFlight.current = false
        setSwitching(false)
      }
      return switched
    },
    [runSwitch],
  )

  return useMemo(
    () => ({ activeTid, blockedReason, switching, switchTo }),
    [activeTid, blockedReason, switching, switchTo],
  )
}
